import { Mutation } from "@apollo/client/react/components";
import { Button } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Hover } from "react-powerplug";
import { refetchQueries } from "~/lib/apollo/RefetchQueriesFixLink";
import DeclineProjectVerification from "~/lib/mutations/DeclineProjectVerification";
import DismissActionCenter from "~/lib/mutations/DismissActionCenter";
import VerifyProject from "~/lib/mutations/VerifyProject";
import {
  IDeclineProjectVerificationMutation,
  IDeclineProjectVerificationMutationVariables,
} from "~/lib/mutations/__generated__/DeclineProjectVerification.generated";
import {
  IDismissActionCenterMutation,
  IDismissActionCenterMutationVariables,
} from "~/lib/mutations/__generated__/DismissActionCenter.generated";
import {
  IVerifyProjectMutation,
  IVerifyProjectMutationVariables,
} from "~/lib/mutations/__generated__/VerifyProject.generated";
import { IGovernmentActionsQuery } from "~/lib/queries/__generated__/GovernmentActionsQuery.generated";
import Strongify from "~/lib/utils/strongify";
import IntlRelativeTime from "~/src/shared/components/IntlRelativeTime";
import { appendNextLinkHrefParams } from "~/src/utils/nextLinkHref";
import { useSelfie } from "~/components/auth/Auth";
import CheckIcon from "~/components/icons/CheckIcon";
import CloseIcon from "~/components/icons/CloseIcon";
import { Margin } from "~/components/spacings";

interface IProjectGovernmentVerificationRequestProps {
  action: Extract<
    NonNullable<IGovernmentActionsQuery["government"]>["actions"][0],
    { __typename: "ProjectGovernmentVerificationRequest" }
  > & {
    verifiedAt?: Date | null;
  };
}

function ProjectGovernmentVerificationRequest({ action }: IProjectGovernmentVerificationRequestProps) {
  const [verifiedProjectState, setVerifiedProjectState] = useState(false);
  const [declinedProjectState, setDeclinedProjectState] = useState(false);
  const viewer = useSelfie();
  const router = useRouter();
  const intl = useIntl();

  // We use both hook and apollo data to check if project is verified/declined
  const verifiedProject = verifiedProjectState || action.status.toUpperCase() === "VERIFIED";
  const declinedProject = declinedProjectState || action.status.toUpperCase() === "DECLINED";

  if (!viewer || viewer.__typename !== "GovernmentUser") {
    return null;
  }

  const { project } = action;

  if (project.__typename === "GovernmentProject") {
    return null;
  }

  const projectType =
    project.__typename === "GroupProject"
      ? intl.formatMessage({ defaultMessage: 'group', id: 'JsQ2Bn' })
      : intl.formatMessage({ defaultMessage: 'vendor', id: 'PjezAc' });

  return (
    <Mutation<IDismissActionCenterMutation, IDismissActionCenterMutationVariables> mutation={DismissActionCenter}>
      {(dismissActionCenter) => (
        <div
          css={{
            display: "flex",
            alignItems: "center",
            padding: 12,
            backgroundColor: palette.white,
            ":hover": {
              backgroundColor: palette.washGray,
            },
          }}
        >
          <ProfilePicture
            size={46}
            image={project.__typename === "GroupProject" ? project.group.logo : project.vendor.logo}
            name={project.__typename === "GroupProject" ? project.group.name : project.vendor.name}
          />

          <Margin ml={12} mr="auto">
            <p
              css={{
                fontSize: 14,
                color: palette.mediumGray,
                margin: 0,
                lineHeight: "18px",
              }}
            >
              {project.author && (
                <Link passHref={true} href="/[userSlug]/[[...tab]]" as={`/@${project.author.slug}`}>
                  <a
                    css={{
                      color: palette.primary,
                      fontWeight: 600,
                      ":hover": {
                        textDecoration: "underline !important",
                      },
                    }}
                  >
                    {project.author.fullName}
                  </a>
                </Link>
              )}{" "}
              <FormattedMessage
                defaultMessage="from {projectType} <strong>{projectName}</strong> has requested to associate your government to the project" id="z51ctt"
                values={{
                  projectType: projectType,
                  projectName: project.__typename === "GroupProject" ? project.group.name : project.vendor.name,
                  strong: (chunk: any) => Strongify(chunk),
                }}
              />{" "}
              <Link
                href={appendNextLinkHrefParams(router, { projectSlug: project.slug })}
                as={`/projects/${project.slug}`}
                passHref={true}
              >
                <a
                  css={{
                    fontWeight: 600,
                    color: palette.primary,
                    ":hover": {
                      textDecoration: "underline !important",
                    },
                  }}
                >
                  {project.title}
                </a>
              </Link>{" "}
              <span
                css={{
                  color: palette.sealBlue,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                }}
              >
                <IntlRelativeTime value={action.createdAt} />
              </span>
            </p>
          </Margin>

          <div
            css={{
              display: "flex",
              width: 170,
              minWidth: 170,
              justifyContent: "flex-end",
            }}
          >
            {verifiedProject && (
              <Margin ml="auto">
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    color: palette.greenBlue,
                  }}
                >
                  <CheckIcon />
                  <Margin ml={6}>
                    <span
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      <FormattedMessage defaultMessage="Verified" id="Z8971h" />
                    </span>
                  </Margin>
                </div>
              </Margin>
            )}

            {declinedProject && (
              <Margin ml="auto">
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    color: palette.red,
                  }}
                >
                  <CloseIcon fill={palette.red} variation="thick" width={15} height={15} />
                  <Margin ml={8}>
                    <span
                      css={{
                        fontSize: 14,
                        fontWeight: 500,
                      }}
                    >
                      <FormattedMessage defaultMessage="Declined" id="uRYIb9" />
                    </span>
                  </Margin>
                </div>
              </Margin>
            )}

            {!verifiedProject && !declinedProject && (
              <div
                css={{
                  display: "grid",
                  gridAutoFlow: "column",
                  alignItems: "center",
                  gridColumnGap: 12,
                }}
              >
                <Mutation<IVerifyProjectMutation, IVerifyProjectMutationVariables>
                  mutation={VerifyProject}
                  refetchQueries={refetchQueries(["VerifiedGovernmentProjects"])}
                >
                  {(verifyProject) => (
                    <Hover>
                      {({ bind, hovered }) => (
                        <Button
                          theme={hovered ? "primary" : "secondary"}
                          size="xsmall"
                          color={palette.innovatorBlue}
                          style={{
                            opacity: 1,
                            backgroundColor: hovered ? palette.innovatorBlue : palette.white,
                            width: 100,
                          }}
                          {...bind}
                          onClick={() => {
                            verifyProject({
                              variables: {
                                governmentId: viewer.government._id,
                                projectId: project._id,
                              },
                            });
                            dismissActionCenter();
                            setVerifiedProjectState(true);
                          }}
                        >
                          <span
                            css={{
                              whiteSpace: "nowrap",
                            }}
                          >
                            <FormattedMessage defaultMessage="Verify Project" id="DLRCfU" />
                          </span>
                        </Button>
                      )}
                    </Hover>
                  )}
                </Mutation>

                <Mutation<IDeclineProjectVerificationMutation, IDeclineProjectVerificationMutationVariables>
                  mutation={DeclineProjectVerification}
                >
                  {(declineProjectVerification) => (
                    <span
                      css={{
                        cursor: "pointer",
                        fontSize: 12,
                        color: palette.red,
                        ":hover": {
                          textDecoration: "underline",
                        },
                      }}
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to decline the verification of ${project.title}?`)) {
                          declineProjectVerification({
                            variables: {
                              governmentId: viewer.government._id,
                              projectId: project._id,
                            },
                          });
                          dismissActionCenter();
                          setDeclinedProjectState(true);
                        }
                      }}
                    >
                      <FormattedMessage id="pvtgR2" defaultMessage="Decline" />
                    </span>
                  )}
                </Mutation>
              </div>
            )}
          </div>
        </div>
      )}
    </Mutation>
  );
}

export default ProjectGovernmentVerificationRequest;
